/* ============================================================
 * XA navigation behaviour.
 * - Global bar (snippets/xa-global-nav.liquid): each top-level item is
 *   a disclosure button (WAI-ARIA disclosure pattern) whose flyout opens
 *   on a click or on hover intent; one animated surface behind every
 *   flyout, a blurred scrim, Esc / outside click / focus-out to close.
 *   Below 1024px the same markup becomes a full-screen sheet with
 *   drill-down panels; the rest of the page is inert while it is open.
 * - The bar hides while scrolling down and returns on scroll up
 *   (not while a menu is open, focus is inside it, or reduced motion
 *   is requested).
 * - Local nav (snippets/xa-local-nav.liquid): on phones the title
 *   toggles the section links; a link scrolls its section's content to
 *   just under the bars; the section in view is highlighted; optional
 *   reading progress.
 * Without JS the desktop flyouts still open on :hover/:focus-within.
 * ============================================================ */
(() => {
  'use strict';

  /* The global and the local nav both include this file; run it once. */
  if (document.documentElement.hasAttribute('data-xa-nav')) return;
  document.documentElement.setAttribute('data-xa-nav', '');

  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 1024px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HOVER_OPEN_DELAY = 140;
  const HOVER_SWITCH_DELAY = 90;
  const HOVER_CLOSE_DELAY = 260;
  const HIDE_AFTER = 160;
  const HIDE_DISTANCE = 24;

  /**
   * @param {Element | null} el
   * @returns {HTMLElement | null}
   */
  const asHTML = (el) => (el instanceof HTMLElement ? el : null);

  /**
   * The theme's cart:update events (CartAddEvent / CartUpdateEvent in
   * assets/events.js) are plain Events with a `detail` property.
   * @typedef {Event & { detail?: { data?: { itemCount?: number, source?: string } } }} CartEvent
   */

  /**
   * Visible links and buttons inside a panel, in DOM order.
   * @param {Element} scope
   * @returns {HTMLElement[]}
   */
  const focusables = (scope) =>
    [...scope.querySelectorAll('a[href], button:not([disabled])')].filter(
      /** @returns {el is HTMLElement} */
      (el) => el instanceof HTMLElement && el.offsetParent !== null
    );

  /** @param {HTMLElement} header */
  function initGlobal(header) {
    if (header.hasAttribute('data-js')) return;
    const nav = asHTML(header.querySelector('[data-xa-gn-nav]'));
    if (!nav) return;
    header.setAttribute('data-js', '');

    /* Every listener goes through this signal, so a theme-editor reload
       of the header section can tear the old instance down completely. */
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;

    /** @type {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement }[]} */
    const entries = [];
    header.querySelectorAll('[data-xa-gn-item]').forEach((node) => {
      const item = asHTML(node);
      const trigger = item && asHTML(item.querySelector('[data-xa-gn-trigger]'));
      const panel = item && asHTML(item.querySelector('[data-xa-gn-panel]'));
      if (item && trigger && panel) entries.push({ item, trigger, panel });
    });
    const menuButton = asHTML(header.querySelector('[data-xa-gn-menu]'));
    const scrim = asHTML(document.querySelector('[data-xa-gn-scrim]'));
    const hasLocalNav = document.querySelector('[data-xa-ln]') !== null;

    /** @type {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement } | null} */
    let current = null;
    /* Opened by hover (closes when the pointer leaves) or pinned by a
       click / keyboard (stays until Esc, outside click or focus-out). */
    let pinned = false;
    let menuOpen = false;
    let openTimer = 0;
    let closeTimer = 0;
    /** The item whose hover-intent timer is running.
        @type {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement } | null} */
    let opening = null;

    function measurePanel() {
      const height = desktop.matches && current ? current.panel.offsetHeight : 0;
      header.style.setProperty('--xa-gn-panel-h', `${height}px`);
    }

    /**
     * @param {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement }} entry
     * @param {{ focus?: boolean, pin?: boolean }} [options]
     */
    function open(entry, { focus = false, pin = false } = {}) {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      opening = null;
      if (current === entry) return;
      if (current) close(current, { quiet: true });
      current = entry;
      pinned = pin;
      entry.item.classList.add('is-open');
      entry.trigger.setAttribute('aria-expanded', 'true');
      if (desktop.matches) {
        header.setAttribute('data-open', 'flyout');
        if (scrim) scrim.setAttribute('data-active', '');
        measurePanel();
      } else {
        nav?.setAttribute('data-level', '1');
      }
      showBar();
      if (focus) {
        const target = desktop.matches
          ? focusables(entry.panel)[0]
          : asHTML(entry.panel.querySelector('[data-xa-gn-back]'));
        if (target) window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
      }
    }

    /**
     * @param {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement }} entry
     * @param {{ quiet?: boolean, returnFocus?: boolean }} [options]
     */
    function close(entry, { quiet = false, returnFocus = false } = {}) {
      window.clearTimeout(openTimer);
      opening = null;
      entry.item.classList.remove('is-open');
      entry.trigger.setAttribute('aria-expanded', 'false');
      if (current === entry) {
        current = null;
        pinned = false;
      }
      if (!quiet) {
        if (desktop.matches) {
          header.removeAttribute('data-open');
          if (scrim) scrim.removeAttribute('data-active');
          header.style.setProperty('--xa-gn-panel-h', '0px');
        } else {
          nav?.removeAttribute('data-level');
        }
      }
      if (returnFocus) entry.trigger.focus({ preventScroll: true });
    }

    /* The sheet makes everything outside the header inert, so focus and
       the accessibility tree stay inside the menu while it is open. */
    /** @type {HTMLElement} */
    let topLevel = header;
    while (topLevel.parentElement && topLevel.parentElement !== document.body) {
      topLevel = topLevel.parentElement;
    }
    /** @param {boolean} on */
    function setPageInert(on) {
      for (const child of document.body.children) {
        const el = asHTML(child);
        if (!el || el === topLevel || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName)) {
          continue;
        }
        if (on && !el.inert) {
          el.inert = true;
          el.setAttribute('data-xa-gn-inert', '');
        } else if (!on && el.hasAttribute('data-xa-gn-inert')) {
          el.inert = false;
          el.removeAttribute('data-xa-gn-inert');
        }
      }
    }

    function openMenu() {
      if (!menuButton) return;
      menuOpen = true;
      header.setAttribute('data-open', 'menu');
      menuButton.setAttribute('aria-expanded', 'true');
      root.classList.add('xa-gn-locked');
      setPageInert(true);
      showBar();
      const first = entries[0];
      if (first) window.requestAnimationFrame(() => first.trigger.focus({ preventScroll: true }));
    }

    /** @param {{ returnFocus?: boolean }} [options] */
    function closeMenu({ returnFocus = false } = {}) {
      if (current) close(current, { quiet: true });
      menuOpen = false;
      nav?.removeAttribute('data-level');
      header.removeAttribute('data-open');
      menuButton?.setAttribute('aria-expanded', 'false');
      root.classList.remove('xa-gn-locked');
      setPageInert(false);
      if (returnFocus) menuButton?.focus({ preventScroll: true });
    }

    function reset() {
      if (menuOpen) closeMenu();
      if (current) close(current);
      header.removeAttribute('data-open');
      if (scrim) scrim.removeAttribute('data-active');
      header.style.setProperty('--xa-gn-panel-h', '0px');
    }

    /* Desktop: hover intent. The first flyout waits a beat so a cursor
       passing over the bar does not flash a panel; switching between
       items while one is open is quicker, but still long enough that a
       diagonal move into the open panel does not switch it.
       Only a pointer that actually moves counts: when the hidden bar
       slides back in under a resting cursor (after scrolling up), or a
       page loads under it, the browser reports the item as entered with
       no movement, and that must not open a panel over the page. The
       position is compared with the previous pointer event (movementX/Y
       is not reported reliably by every engine); only the very first
       event of a page falls back to movementX/Y.
       A flyout closed by a click or Esc while the cursor rests on its
       item stays closed until the cursor leaves the item, so the next
       twitch of the mouse does not bring it back. */
    let pointerX = Number.NaN;
    let pointerY = Number.NaN;
    window.addEventListener(
      'pointermove',
      (event) => {
        /* Runs after the item listeners (bubbling ends at window), so they
           still see the previous position. */
        pointerX = event.clientX;
        pointerY = event.clientY;
      },
      { passive: true, signal }
    );
    /** @type {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement } | null} */
    let suppressed = null;
    /** @param {{ item: HTMLElement, trigger: HTMLElement, panel: HTMLElement }} entry */
    const suppressHover = (entry) => {
      if (entry.item.matches(':hover')) suppressed = entry;
    };
    entries.forEach((entry) => {
      entry.item.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse' || !desktop.matches) return;
          const moved = Number.isNaN(pointerX)
            ? event.movementX !== 0 || event.movementY !== 0
            : event.clientX !== pointerX || event.clientY !== pointerY;
          if (!moved) return;
          window.clearTimeout(closeTimer);
          if (current === entry || opening === entry || suppressed === entry) return;
          window.clearTimeout(openTimer);
          opening = entry;
          openTimer = window.setTimeout(
            () => {
              opening = null;
              open(entry, { pin: pinned });
            },
            current ? HOVER_SWITCH_DELAY : HOVER_OPEN_DELAY
          );
        },
        { signal }
      );
      entry.item.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          window.clearTimeout(openTimer);
          opening = null;
          if (suppressed === entry) suppressed = null;
        },
        { signal }
      );
      entry.trigger.addEventListener(
        'click',
        (event) => {
          /* A mouse click on a flyout that hover already opened pins it
             instead of closing it under the cursor. */
          if (current === entry && !pinned && event.detail > 0 && desktop.matches) {
            pinned = true;
            return;
          }
          if (current === entry) {
            close(entry);
            suppressHover(entry);
          } else {
            open(entry, { focus: !desktop.matches, pin: true });
          }
        },
        { signal }
      );
      const back = entry.panel.querySelector('[data-xa-gn-back]');
      if (back) back.addEventListener('click', () => close(entry, { returnFocus: true }), { signal });
    });

    header.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse') window.clearTimeout(closeTimer);
      },
      { signal }
    );
    header.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType !== 'mouse' || !desktop.matches || !current || pinned) return;
        window.clearTimeout(openTimer);
        closeTimer = window.setTimeout(() => {
          if (current && !pinned) close(current);
        }, HOVER_CLOSE_DELAY);
      },
      { signal }
    );

    header.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (current) {
          event.preventDefault();
          const closing = current;
          close(closing, { returnFocus: true });
          suppressHover(closing);
        } else if (menuOpen) {
          event.preventDefault();
          closeMenu({ returnFocus: true });
        }
        return;
      }
      if (event.key === 'ArrowDown' && desktop.matches && event.target instanceof Element) {
        const trigger = event.target.closest('[data-xa-gn-trigger]');
        const entry = entries.find((candidate) => candidate.trigger === trigger);
        if (!entry) return;
        event.preventDefault();
        if (current === entry) {
          pinned = true;
          focusables(entry.panel)[0]?.focus();
        } else {
          open(entry, { focus: true, pin: true });
        }
      }
    }, { signal });

    /* Focus moving out of an open flyout (Tab past its last link, or to
       another top-level item) closes it. Clicks on empty panel space blur
       to <body> (relatedTarget null) and are ignored. */
    header.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (!desktop.matches || !current || !(next instanceof Node)) return;
        if (!current.item.contains(next)) close(current);
      },
      { signal }
    );
    header.addEventListener('focusin', () => showBar(), { signal });

    /* Outside clicks close the flyout. On the scrim this waits for the
       click itself: closing on pointerdown takes the scrim out of hit
       testing before the tap's click arrives, and on a touch screen that
       click would then activate whatever lies beneath. */
    scrim?.addEventListener(
      'click',
      () => {
        if (current) close(current);
      },
      { signal }
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!desktop.matches || !current || event.target === scrim) return;
        if (!(event.target instanceof Node) || !header.contains(event.target)) close(current);
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && current && !header.contains(document.activeElement)) {
          const closing = current;
          close(closing);
          suppressHover(closing);
        }
      },
      { signal }
    );

    menuButton?.addEventListener('click', () => (menuOpen ? closeMenu() : openMenu()), { signal });

    /* Links into the current page (anchors) close the menus first. */
    nav.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
        if (!(link instanceof HTMLAnchorElement)) return;
        const url = new URL(link.href, window.location.href);
        if (url.pathname === window.location.pathname && url.hash) {
          if (menuOpen) closeMenu();
          else if (current) close(current);
        }
      },
      { signal }
    );

    /* Cart count. Products are added with fetch (cart_type "page"), so the
       count follows the theme's cart:update events. /cart.js gives the real
       total: a product form's event carries only the quantity it added, and
       it is sent even when the add partly failed. */
    const cartLink = header.querySelector('[data-xa-gn-cart]');
    const cartUrl = cartLink instanceof HTMLAnchorElement ? `${cartLink.pathname}.js` : null;
    const cartCount = asHTML(header.querySelector('[data-xa-gn-cart-count]'));
    const cartLabel = asHTML(header.querySelector('[data-xa-gn-cart-label]'));
    let cartTotal = Number(cartLink?.getAttribute('data-count')) || 0;
    let cartRequest = 0;

    /** @param {number} count */
    function renderCart(count) {
      cartTotal = count;
      if (cartCount) {
        cartCount.hidden = count < 1;
        cartCount.textContent = count > 99 ? '99+' : String(count);
      }
      if (cartLabel) cartLabel.textContent = count > 0 ? `カート（${count}点）` : 'カート';
    }

    /** @param {number | null} estimate - shown when /cart.js cannot be read */
    async function refreshCart(estimate) {
      if (!cartUrl) return;
      const request = ++cartRequest;
      let count = estimate;
      try {
        const response = await fetch(cartUrl, {
          headers: { Accept: 'application/json' },
          signal,
        });
        if (response.ok) {
          const cart = await response.json();
          if (typeof cart?.item_count === 'number') count = cart.item_count;
        }
      } catch {
        /* Offline or torn down: keep the estimate. */
      }
      if (signal.aborted || request !== cartRequest || count === null) return;
      renderCart(count);
    }

    document.addEventListener(
      'cart:update',
      (event) => {
        const data = /** @type {CartEvent} */ (event).detail?.data ?? {};
        const quantity = Number(data.itemCount) || 0;
        /* Horizon's rule for the estimate: a product form reports the
           quantity it added, every other source the new total. */
        refreshCart(data.source === 'product-form-component' ? cartTotal + quantity : quantity);
      },
      { signal }
    );

    desktop.addEventListener('change', reset, { signal });
    window.addEventListener(
      'resize',
      () => {
        if (current && desktop.matches) measurePanel();
      },
      { signal }
    );
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) return;
        reset();
        /* A page restored from the back/forward cache still shows the
           count it had when the visitor left it. */
        refreshCart(null);
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (!(event.target instanceof Node) || !event.target.contains(header)) return;
        reset();
        showBar();
        lifecycle.abort();
      },
      { signal }
    );

    /* Surface state and hide-on-scroll. The bar hides after the reader
       has moved HIDE_DISTANCE px down since the last change of direction
       and comes back after the same distance up, so slow trackpad
       scrolling behaves like a flick. */
    let lastY = window.scrollY;
    let direction = 0;
    let turnY = lastY;
    let ticking = false;
    function hideBar() {
      if (header.hasAttribute('data-hidden')) return;
      header.setAttribute('data-hidden', '');
      root.classList.add('xa-gn-is-hidden');
    }
    function showBar() {
      if (!header.hasAttribute('data-hidden')) return;
      header.removeAttribute('data-hidden');
      root.classList.remove('xa-gn-is-hidden');
    }
    function onScroll() {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      header.toggleAttribute('data-scrolled', y > 8 || hasLocalNav);
      const step = Math.sign(y - lastY);
      if (step !== 0 && step !== direction) {
        direction = step;
        turnY = lastY;
      }
      lastY = y;
      const busy =
        current !== null || menuOpen || header.contains(document.activeElement) || reduceMotion.matches;
      if (y <= HIDE_AFTER || (direction < 0 && turnY - y > HIDE_DISTANCE)) showBar();
      else if (direction > 0 && y - turnY > HIDE_DISTANCE && !busy) hideBar();
    }
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(onScroll);
      },
      { passive: true, signal }
    );
    onScroll();
  }

  /** @param {HTMLElement} localNav */
  function initLocal(localNav) {
    if (localNav.hasAttribute('data-js')) return;
    localNav.setAttribute('data-js', '');
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (event.target instanceof Node && event.target.contains(localNav)) lifecycle.abort();
      },
      { signal }
    );

    const toggle = asHTML(localNav.querySelector('[data-xa-ln-toggle]'));
    /** @type {HTMLAnchorElement[]} */
    const links = [...localNav.querySelectorAll('a[data-xa-ln-link]')].filter(
      /** @returns {el is HTMLAnchorElement} */
      (el) => el instanceof HTMLAnchorElement
    );
    const progress = localNav.querySelector('[data-xa-ln-progress]');

    if (toggle) {
      /**
       * @param {boolean} isOpen
       * @param {boolean} [returnFocus]
       */
      const setOpen = (isOpen, returnFocus = false) => {
        toggle.setAttribute('aria-expanded', String(isOpen));
        localNav.toggleAttribute('data-open', isOpen);
        if (returnFocus) toggle.focus({ preventScroll: true });
      };
      toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'), { signal });
      links.forEach((link) => link.addEventListener('click', () => setOpen(false), { signal }));
      localNav.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && localNav.hasAttribute('data-open')) setOpen(false, true);
        },
        { signal }
      );
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (!localNav.hasAttribute('data-open')) return;
          if (!(event.target instanceof Node) || !localNav.contains(event.target)) setOpen(false);
        },
        { signal }
      );
      desktop.addEventListener('change', () => setOpen(false), { signal });
    }

    /* Ids of the sections the links point to on this page; a link to
       another page is left to the browser. The elements are looked up
       when needed, so a section the theme editor re-renders is found
       again. */
    const ids = links.map((link) => {
      if (link.pathname !== window.location.pathname || link.search !== window.location.search) return '';
      return decodeURIComponent(link.hash.slice(1));
    });
    /** @param {number} index */
    const sectionAt = (index) => {
      const id = ids[index];
      return id ? asHTML(document.getElementById(id)) : null;
    };

    /* Highlighting. The active link is the last section whose top has
       scrolled up to just under the bars; at the very end of the page it
       is the last section in view (a short final section never reaches
       the bars). A link that was just clicked stays active until the
       scroll it started settles. */
    /** @param {number} index */
    const setActive = (index) => {
      links.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    let clicked = -1;
    let settleTimer = 0;
    let spyTicking = false;
    function spy() {
      spyTicking = false;
      if (clicked !== -1) return;
      const line = localNav.getBoundingClientRect().bottom + 24;
      let active = -1;
      ids.forEach((_, index) => {
        const section = sectionAt(index);
        if (section && section.getBoundingClientRect().top <= line) active = index;
      });
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        for (let i = ids.length - 1; i >= 0; i -= 1) {
          const section = sectionAt(i);
          if (section && section.getBoundingClientRect().top < window.innerHeight) {
            active = i;
            break;
          }
        }
      }
      setActive(active);
    }
    if (ids.some(Boolean)) {
      window.addEventListener(
        'scroll',
        () => {
          window.clearTimeout(settleTimer);
          settleTimer = window.setTimeout(() => {
            clicked = -1;
            spy();
          }, 160);
          if (spyTicking) return;
          spyTicking = true;
          window.requestAnimationFrame(spy);
        },
        { passive: true, signal }
      );
      window.addEventListener('resize', spy, { passive: true, signal });
      spy();
    }

    /* Scrolling to a section. The browser would put the section's top
       under the bars, which leaves its top padding as a gap above the
       heading; instead the first content inside it lands just below the
       bars — the global bar slides away when scrolling down, so only the
       local nav is counted then. Focus moves to the section, as a native
       anchor jump would move the starting point for Tab and reading. */
    /**
     * Document position of an element, ignoring transforms (reveal
     * animations move content while it is still off screen).
     * @param {HTMLElement} el
     */
    const documentTop = (el) => {
      let top = 0;
      for (let node = /** @type {HTMLElement | null} */ (el); node; node = asHTML(node.offsetParent)) {
        top += node.offsetTop;
      }
      return top;
    };
    /**
     * @param {Element} el
     * @param {Element} section
     */
    const inFlow = (el, section) => {
      for (let node = /** @type {Element | null} */ (el); node && node !== section; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if (['absolute', 'fixed'].includes(style.position) || style.display === 'none' || style.visibility === 'hidden') return false;
        if (node.getAttribute('aria-hidden') === 'true') return false;
      }
      return true;
    };
    /** @param {HTMLElement} section */
    const contentTop = (section) => {
      const walker = document.createTreeWalker(section, NodeFilter.SHOW_ELEMENT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const el = asHTML(/** @type {Element} */ (node));
        if (!el) continue;
        const hasText = [...el.childNodes].some((child) => child.nodeType === Node.TEXT_NODE && child.textContent?.trim());
        if (!hasText && !['IMG', 'VIDEO', 'PICTURE', 'CANVAS'].includes(el.tagName)) continue;
        if (el.offsetWidth === 0 || el.offsetHeight === 0 || !inFlow(el, section)) continue;
        return documentTop(el);
      }
      return documentTop(section);
    };
    const globalBarHeight = () => parseFloat(window.getComputedStyle(root).getPropertyValue('--xa-gn-h')) || 0;

    links.forEach((link, index) => {
      link.addEventListener(
        'click',
        (event) => {
          const section = sectionAt(index);
          if (!section || event.defaultPrevented || event.button !== 0) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const hasGlobalBar = document.querySelector('[data-xa-gn]') !== null;
          const top = contentTop(section);
          const lnHeight = localNav.offsetHeight;
          /* Without the XA bar the local nav sits under Horizon's header. */
          const headerHeight = hasGlobalBar ? globalBarHeight() : Math.max(0, localNav.getBoundingClientRect().top);
          const withBar = top - lnHeight - headerHeight - 20;
          const withoutBar = top - lnHeight - 20;
          /* Mirrors the hide-on-scroll rule of the global bar: it slides
             away after more than HIDE_DISTANCE px down past HIDE_AFTER,
             returns after as much up, and stays put with reduced motion. */
          let barGone = false;
          if (hasGlobalBar && !reduceMotion.matches && withoutBar > HIDE_AFTER) {
            const travel = withoutBar - window.scrollY;
            barGone = root.classList.contains('xa-gn-is-hidden') ? travel > -HIDE_DISTANCE : travel > HIDE_DISTANCE * 2;
          }
          const destination = barGone ? withoutBar : withBar;
          clicked = index;
          setActive(index);
          window.scrollTo({ top: Math.max(0, Math.round(destination)), behavior: reduceMotion.matches ? 'instant' : 'smooth' });
          if (link.hash && window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
          /* A temporary tabindex makes the section focusable; it goes again
             when focus leaves, so later clicks inside the section do not
             focus the section itself. A section that had its own tabindex
             keeps it, and its own focus ring. */
          if (!section.hasAttribute('tabindex')) {
            section.setAttribute('tabindex', '-1');
            section.setAttribute('data-xa-ln-target', '');
            section.addEventListener(
              'blur',
              () => {
                section.removeAttribute('tabindex');
                section.removeAttribute('data-xa-ln-target');
              },
              { once: true }
            );
          }
          section.focus({ preventScroll: true });
          window.clearTimeout(settleTimer);
          settleTimer = window.setTimeout(() => {
            clicked = -1;
            spy();
          }, 1200);
        },
        { signal }
      );
    });

    if (progress) {
      let ticking = false;
      const update = () => {
        ticking = false;
        const total = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0;
        localNav.style.setProperty('--xa-ln-progress', ratio.toFixed(4));
      };
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        },
        { passive: true, signal }
      );
      update();
    }
  }

  function init() {
    const header = asHTML(document.querySelector('[data-xa-gn]'));
    if (header) initGlobal(header);
    document.querySelectorAll('[data-xa-ln]').forEach((node) => {
      const localNav = asHTML(node);
      if (localNav) initLocal(localNav);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
